
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Standards from "./pages/Standards";
import StandardDetail from "./pages/StandardDetail";
import ProjectDetail from "./pages/ProjectDetail";
import PortfolioDetail from "./pages/PortfolioDetail";
import ProductDetail from "./pages/ProductDetail";
import ProductDetailDirectory from "./pages/ProductDetailDirectory";
import CertificationManagement from "./pages/CertificationManagement";
import Directory from "./pages/Directory";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { PersonaProvider } from "./contexts/PersonaContext";

// Persona pages
import Client from "./pages/Client";
import Reviewer from "./pages/Reviewer";
import StandardManager from "./pages/StandardManager";
import BrandUser from "./pages/BrandUser";
import BrandBenchmarks from "./pages/BrandBenchmarks";
import Admin from "./pages/Admin";
import GSClientPortal from "./pages/GSClientPortal";
import GSProductDetail from "./pages/GSProductDetail";
import Messages from "./pages/Messages";
import ReviewDetail from "./pages/ReviewDetail";
import CertManager from "./pages/CertManager";
import GSDocumentTool from "./pages/GSDocumentTool";
import GSCertClient from "./pages/GSCertClient";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PersonaProvider>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/standards" 
              element={
                <ProtectedRoute>
                  <Standards />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/standards/:standardId" 
              element={
                <ProtectedRoute>
                  <StandardDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/projects/:projectId" 
              element={
                <ProtectedRoute>
                  <ProjectDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/directory" 
              element={
                <ProtectedRoute>
                  <Directory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/directory/products/:productId" 
              element={
                <ProtectedRoute>
                  <ProductDetailDirectory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/messages" 
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              } 
            />
            
            {/* Persona pages */}
            <Route 
              path="/client" 
              element={
                <ProtectedRoute>
                  <Client />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/client/portfolios/:portfolioId" 
              element={
                <ProtectedRoute>
                  <PortfolioDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/client/portfolios/:portfolioId/products/:productId" 
              element={
                <ProtectedRoute>
                  <ProductDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/client/portfolios/:portfolioId/products/:productId/certification" 
              element={
                <ProtectedRoute>
                  <CertificationManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reviewer" 
              element={
                <ProtectedRoute>
                  <Reviewer />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/review/:reviewId" 
              element={
                <ProtectedRoute>
                  <ReviewDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/cert-manager" 
              element={
                <ProtectedRoute>
                  <CertManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/standard-manager" 
              element={
                <ProtectedRoute>
                  <StandardManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/brand-user" 
              element={
                <ProtectedRoute>
                  <BrandUser />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/brand-user/benchmarks" 
              element={
                <ProtectedRoute>
                  <BrandBenchmarks />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-client-portal" 
              element={
                <ProtectedRoute>
                  <GSClientPortal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-client-portal/product/:productId" 
              element={
                <ProtectedRoute>
                  <GSProductDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-document-tool" 
              element={
                <ProtectedRoute>
                  <GSDocumentTool />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-document-tool/products/:productId" 
              element={
                <ProtectedRoute>
                  <GSProductDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-cert-client" 
              element={
                <ProtectedRoute>
                  <GSCertClient />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-cert-client/portfolios/:portfolioId" 
              element={
                <ProtectedRoute>
                  <PortfolioDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-cert-client/portfolios/:portfolioId/products/:productId" 
              element={
                <ProtectedRoute>
                  <ProductDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gs-cert-client/portfolios/:portfolioId/products/:productId/certification" 
              element={
                <ProtectedRoute>
                  <CertificationManagement />
                </ProtectedRoute>
              } 
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PersonaProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
